import React, { useMemo, useState } from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Icons from '../Icons';
import { formatDate } from '../safeguarding/safeguardingUtils';
import { getFollowUpStatus } from './incidentUtils';

const severityLabels = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low'
};

const severityVariants = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low'
};

const statusConfig = {
  open: { label: 'Open', variant: 'review', icon: Icons.Clock },
  monitoring: { label: 'Monitoring', variant: 'review', icon: Icons.Clock },
  closed: { label: 'Closed', variant: 'inactive', icon: Icons.Check }
};

export default function IncidentDetailDrawer({
  incident,
  participantLookup,
  isOpen,
  onClose,
  onAssign,
  onSetFollowUp,
  onCloseIncident,
  onUpdateNotes,
  onViewHistory
}) {
  const [editingNotes, setEditingNotes] = useState(false);
  const followUpStatus = incident ? getFollowUpStatus(incident) : { isOverdue: false, isDueSoon: false };

  const statusKey = incident ? String(incident.status || 'open').toLowerCase() : 'open';
  const status = statusConfig[statusKey] || statusConfig.open;
  const StatusIcon = status.icon;

  const students = useMemo(() => {
    if (!incident) return [];
    return (incident.participants || [])
      .filter((participant) => participant.type === 'student')
      .map((participant) => participant.name || participantLookup?.[participant.id] || participant.id);
  }, [incident, participantLookup]);

  const timeline = useMemo(() => {
    if (!incident) return [];
    const events = [
      { id: 'reported', label: 'Incident reported', date: incident.date },
      incident.followUpDate
        ? { id: 'followup', label: 'Follow-up scheduled', date: incident.followUpDate }
        : null,
      incident.updatedAt && incident.updatedAt !== incident.createdAt
        ? { id: 'updated', label: 'Updated', date: incident.updatedAt }
        : null
    ].filter(Boolean);
    return events.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [incident]);

  if (!incident) {
    return (
      <Card className={`sp-inventory-drawer ${isOpen ? 'is-open' : ''}`}>
        <div className="sp-drawer-empty">
          <p className="sp-value" style={{ fontWeight: 600 }}>Select an incident</p>
          <p className="text-sm sp-muted">Choose an incident to see details and actions.</p>
        </div>
      </Card>
    );
  }

  const severityKey = String(incident.severity || 'medium').toLowerCase();

  return (
    <Card className={`sp-inventory-drawer ${isOpen ? 'is-open' : ''}`} aria-label="Incident detail panel">
      <div className="sp-drawer-header">
        <div>
          <div className="sp-value" style={{ fontWeight: 600 }}>{incident.type}</div>
          <div className="text-xs sp-muted">{formatDate(incident.date)} · {incident.reportedBy || 'Reported by system'}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close incident panel">
          Close
        </Button>
      </div>

      <div className="sp-drawer-section">
        <div className="sp-drawer-badges">
          <Badge variant={severityVariants[severityKey] || 'medium'}>
            {severityLabels[severityKey] || incident.severity}
          </Badge>
          <Badge variant={status.variant}>
            <span aria-hidden="true">
              <StatusIcon />
            </span>
            {status.label}
          </Badge>
          {followUpStatus.isOverdue && (
            <Badge variant="danger">
              <Icons.Alert /> Overdue follow-up
            </Badge>
          )}
        </div>
        <div className="sp-drawer-meta">
          <span>
            <span className="sp-meta-label">Students</span>
            <span>{students.length ? students.join(', ') : 'No students listed'}</span>
          </span>
          <span>
            <span className="sp-meta-label">Assigned</span>
            <span>{incident.assignedKeyWorker || 'Unassigned'}</span>
          </span>
          <span>
            <span className="sp-meta-label">Follow-up</span>
            <span>
              {incident.followUpRequired ? formatDate(incident.followUpDate) || 'Required' : 'None'}
            </span>
          </span>
        </div>
      </div>

      <div className="sp-drawer-section sp-drawer-actions">
        <Button size="sm" variant="primary" onClick={() => setEditingNotes(true)}>
          Update / Add note
        </Button>
        <Button size="sm" variant="secondary" onClick={() => onAssign?.(incident)}>
          Assign staff
        </Button>
        <Button size="sm" variant="secondary" onClick={() => onSetFollowUp?.(incident)}>
          Set follow-up
        </Button>
        {incident.status !== 'closed' && (
          <Button size="sm" variant="ghost" onClick={() => onCloseIncident?.(incident)}>
            Close incident
          </Button>
        )}
      </div>

      <div className="sp-drawer-section">
        <div className="flex items-center justify-between">
          <div className="sp-value" style={{ fontWeight: 600 }}>Notes</div>
          {editingNotes && (
            <Button size="sm" variant="ghost" onClick={() => setEditingNotes(false)}>
              Done
            </Button>
          )}
        </div>
        {editingNotes ? (
          <div className="space-y-2">
            <textarea
              key={incident.id}
              className="sp-textarea w-full"
              rows={4}
              defaultValue={incident.description}
              onBlur={(event) => onUpdateNotes?.(incident, event.target.value)}
              aria-label="Incident notes"
            />
            <p className="text-xs sp-muted">Notes save when you leave the field.</p>
          </div>
        ) : (
          <p className="text-sm sp-muted">{incident.description || 'No notes recorded.'}</p>
        )}
      </div>

      <div className="sp-drawer-section">
        <div className="flex items-center justify-between">
          <div className="sp-value" style={{ fontWeight: 600 }}>Timeline</div>
          <Button size="sm" variant="ghost" onClick={() => onViewHistory?.(incident)}>
            View audit history
          </Button>
        </div>
        <div className="sp-drawer-timeline">
          {timeline.length === 0 && <p className="text-sm sp-muted">No updates recorded yet.</p>}
          {timeline.map((event) => (
            <div key={event.id} className="sp-drawer-timeline-item">
              <span className="sp-meta-label">{formatDate(event.date)}</span>
              <span>{event.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
